import KeyboardVoice from "@mui/icons-material/KeyboardVoice";
import { darken, useTheme } from "@mui/material";

import { Button } from "../index.js";

/**
 * One wording for every dictation launcher. Held here rather than passed in so
 * the per-field and form-level entry points cannot drift apart.
 */
const DICTATION_BUTTON_LABEL = "Launch dictation";

export type TortusDictationButtonProps = {
  onClick: () => void;
  testId: string;
};

/**
 * The button that starts a Tortus dictation, wherever it appears — on a single
 * field or at the top of a form. Shared so the entry points cannot drift apart.
 *
 * The fill is `success.main` rather than the brighter `primary.lightGreen`
 * because the label and icon sit on it in white: the brighter green gives 2.4:1
 * contrast, which fails WCAG AA, while this one clears it at 4.6:1.
 *
 * Every colour is pinned here because the house Button's theme overrides fight a
 * custom fill: `MuiButton.root` forces a blue hover background, and
 * `MuiButton.startIcon` forces a dark icon. So the fill, the label colour and the
 * icon colour are each restated for both resting and hover states.
 */
export const TortusDictationButton = ({
  onClick,
  testId,
}: TortusDictationButtonProps) => {
  const theme = useTheme();
  const fill = theme.palette.success.main;
  const onFill = theme.palette.common.white;

  return (
    <Button
      contained
      small
      label={DICTATION_BUTTON_LABEL}
      testId={testId}
      onClick={onClick}
      customStartIcon={<KeyboardVoice fontSize="small" />}
      sx={{
        backgroundColor: fill,
        color: onFill,
        "& .MuiButton-startIcon svg": { color: onFill },
        "&:hover": {
          backgroundColor: darken(fill, 0.12),
          color: onFill,
          "& .MuiButton-startIcon svg": { color: onFill },
        },
      }}
    />
  );
};
